// @flow

class LICache {
    
    static _db: any;
    static DB_NAME: string = "li-ro-cache";
    static STORE_NAME: string = "kv";
    static KEY_RETRIEVAL_INFO: string = "retrievalInfo";
    
    constructor() {
        (async () => LICache.openDBAsync())(); //IIFE
    }
    
    static async openDBAsync() {
        if (!('indexedDB' in window)) {
            console.error("*** IndexedDB NOT SUPPORTED ***");
            alert("IndexedDB needs to be supported by the browser");
            return;
        }

        if (LICache._db) return;

        LICache._db = await new Promise((resolve, reject) => {
            const request = window.indexedDB.open(LICache.DB_NAME, 1);

            request.onupgradeneeded = (_) => {
                const db = request.result;
                if (!db.objectStoreNames.contains(LICache.STORE_NAME)) {
                    db.createObjectStore(LICache.STORE_NAME);
                }
            };

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                console.error("IndexedDB open error", request.error);
                reject(request.error);
            };
        });
        console.log("IndexedDB opened", LICache._db);
    }
    
    static async ensureDBIsOpen() {
        if (LICache._db) {
            console.debug("IndexedDB is already open");
            return;
        }

        console.log("opening IndexedDB (ensureDBIsOpen)");
        await LICache.openDBAsync();
    }
    
    static async deleteCache() {
        console.debug("*** DELETE IndexedDB ***");
        if ('indexedDB' in window) {
            await new Promise((resolve, reject) => {
                const req = window.indexedDB.deleteDatabase(LICache.DB_NAME);
                req.onsuccess = () => resolve(true);
                req.onerror = () => reject(req.error);
                req.onblocked = () => {
                    console.warn("Delete DB blocked; close other tabs.");
                };
            });
            LICache._db = null;
        }
    }

    static async getRetrievalInfo():Object {
        await LICache.ensureDBIsOpen();
        console.debug("getting ROInfo (idb)...");
        const val = await LICache._idbGet(LICache.KEY_RETRIEVAL_INFO);
        if (val === undefined || val === null || val === "") return {};
        return val;
    }

    static async setRetrievalInfo(rInfo:Object) {
        await LICache.ensureDBIsOpen();
        console.debug("saving ROInfo (idb)...");
        await LICache._idbPut(LICache.KEY_RETRIEVAL_INFO, rInfo);
    }

    static async clearRetrievalInfo(initialValue={}) {
        console.debug("clearing RO (idb)...");
        await LICache.setRetrievalInfo(initialValue);
    }

    static async clearAll() {
        await LICache.setRetrievalInfo(null);
    }

    static async getRetrievalSampleList():Promise<Array> {
        const rInfo = await LICache.getRetrievalInfo();
        return rInfo?.samples??[]; // grab the samples out of the general info (easier)
    }

    static async setRetrievalSampleList(samples:Array, updateSavedFlagTo:boolean|null=null) {
        const rInfo = await LICache.getRetrievalInfo();
        rInfo.samples = samples;
        if(updateSavedFlagTo!==null) {
            rInfo.saved=updateSavedFlagTo;
        }
        await LICache.setRetrievalInfo(rInfo); // updated
    }
    
    static async getRetrievalOrderId():number {
        let rInfo = await LICache.getRetrievalInfo();
        return rInfo?.orderId??-1;
    }

    static async addSamplesToRetrievalList(samples:Array) {
        let addedOne=false;
        const curList = await LICache.getRetrievalSampleList();
        for(let i=0;i<samples.length;i++) { // check for duplicates
            if(!curList.find(item => item.id===samples[i].id)) { // add
                curList.push(samples[i]);
                addedOne=true;
            }
        }
        await LICache.setRetrievalSampleList(curList,addedOne?false:null); // null here means "don't update"
    }
    
    static async removeSamplesFromRetrievalList(samplesToRemove: Array) {
        console.debug("removing samples from retrieval list...");
        const curList = await LICache.getRetrievalSampleList();

        const updatedList = curList.filter((item) => !samplesToRemove.find((sample) => sample.id === item.id));

        const removedAny = curList.length !== updatedList.length;
        await LICache.setRetrievalSampleList(updatedList, removedAny ? false : null); // null means "don't update saved flag"
    }

    /**
     * set the complete text for the RO to that which is provided.
     * this is used for updating a note visually after the server has had a note added to it
     * after a RO is completed.
     * @param fullNoteText
     * @returns {Promise<void>}
     */
    static async updateRetrievalNote(fullNoteText:string) {
        const roInfo = await LICache.getRetrievalInfo();
        roInfo.notes = fullNoteText;
        await LICache.setRetrievalInfo(roInfo);
    }

    // --- Internal IDB helpers ---
    static _getStore(mode: any): any {
        const db = LICache._db;
        if (!db) {
            throw new Error('IndexedDB not open');
        }
        const tx = db.transaction(LICache.STORE_NAME, mode);
        return tx.objectStore(LICache.STORE_NAME);
    }

    static async _idbGet(key: string): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                const store = LICache._getStore('readonly');
                const req = store.get(key);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            } catch (e) {
                reject(e);
            }
        });
    }

    static async _idbPut(key: string, value: any): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                const store = LICache._getStore('readwrite');
                const req = store.put(value, key);
                req.onsuccess = () => resolve();
                req.onerror = () => reject(req.error);
            } catch (e) {
                reject(e);
            }
        });
    }
}

export default LICache;